export type SceneAdjustments = {
  version: 1
  enabled: boolean
  exposure: number
  brightness: number
  contrast: number
  vibrance: number
  saturation: number
  warmth: number
  sharpness: number
  preset: string
  updated_at: string
  thumbnail_url?: string | null
}

export type SceneAdjustmentInput = Partial<Omit<SceneAdjustments, 'version' | 'updated_at'>> & {
  updated_at?: string
}

export const DEFAULT_SCENE_ADJUSTMENTS: SceneAdjustments = {
  version: 1,
  enabled: false,
  exposure: 0,
  brightness: 0,
  contrast: 0,
  vibrance: 0,
  saturation: 0,
  warmth: 0,
  sharpness: 0,
  preset: 'none',
  updated_at: '',
  thumbnail_url: null,
}

function clamp(value: unknown, min: number, max: number, fallback = 0): number {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.max(min, Math.min(max, Math.round(numeric)))
}

export function parseSceneMeta(value: unknown): Record<string, any> {
  if (!value) return {}
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  return typeof value === 'object' && !Array.isArray(value) ? { ...(value as Record<string, any>) } : {}
}

export function normalizeSceneAdjustments(value: unknown): SceneAdjustments {
  const source = parseSceneMeta(value)
  const raw = source.adjustments && typeof source.adjustments === 'object'
    ? source.adjustments
    : source

  const normalized: SceneAdjustments = {
    version: 1,
    enabled: raw.enabled === true,
    exposure: clamp(raw.exposure, -100, 100),
    brightness: clamp(raw.brightness, -100, 100),
    contrast: clamp(raw.contrast, -100, 100),
    vibrance: clamp(raw.vibrance, -100, 100),
    saturation: clamp(raw.saturation, -100, 100),
    warmth: clamp(raw.warmth, -50, 50),
    sharpness: clamp(raw.sharpness, 0, 100),
    preset: typeof raw.preset === 'string' && raw.preset.trim() ? raw.preset.trim().slice(0, 40) : 'custom',
    updated_at: typeof raw.updated_at === 'string' ? raw.updated_at : '',
    thumbnail_url: typeof raw.thumbnail_url === 'string' && raw.thumbnail_url.trim() ? raw.thumbnail_url.trim() : null,
  }

  normalized.enabled = normalized.enabled && hasAnySceneAdjustments(normalized)
  if (!normalized.enabled) return { ...DEFAULT_SCENE_ADJUSTMENTS }
  return normalized
}

export function createSceneAdjustments(input: SceneAdjustmentInput = {}): SceneAdjustments {
  const adjustments = normalizeSceneAdjustments({
    ...DEFAULT_SCENE_ADJUSTMENTS,
    ...input,
    enabled: true,
    updated_at: input.updated_at || new Date().toISOString(),
  })
  return hasAnySceneAdjustments(adjustments) ? adjustments : { ...DEFAULT_SCENE_ADJUSTMENTS }
}

export function hasRuntimeSceneAdjustments(adjustments: SceneAdjustments): boolean {
  return !!adjustments.enabled && (
    adjustments.exposure !== 0 ||
    adjustments.brightness !== 0 ||
    adjustments.contrast !== 0 ||
    adjustments.vibrance !== 0 ||
    adjustments.saturation !== 0 ||
    adjustments.warmth !== 0 ||
    adjustments.sharpness !== 0
  )
}

export function hasAnySceneAdjustments(adjustments: Pick<SceneAdjustments, 'exposure' | 'brightness' | 'contrast' | 'vibrance' | 'saturation' | 'warmth' | 'sharpness'>): boolean {
  return adjustments.exposure !== 0 ||
    adjustments.brightness !== 0 ||
    adjustments.contrast !== 0 ||
    adjustments.vibrance !== 0 ||
    adjustments.saturation !== 0 ||
    adjustments.warmth !== 0 ||
    adjustments.sharpness !== 0
}

export function setSceneAdjustmentMeta(meta: unknown, adjustments: SceneAdjustments): Record<string, any> {
  const next = parseSceneMeta(meta)
  delete next.adjust_filters
  delete next.adjust_preset

  if (adjustments.enabled && hasAnySceneAdjustments(adjustments)) {
    next.adjustments = { ...adjustments, updated_at: adjustments.updated_at || new Date().toISOString() }
  } else {
    delete next.adjustments
  }

  return next
}

export function sceneAdjustmentThumbnailUrl(meta: unknown): string | null {
  const adjustments = normalizeSceneAdjustments(meta)
  return adjustments.enabled && adjustments.thumbnail_url ? adjustments.thumbnail_url : null
}

export function clearSceneAdjustmentMeta(meta: unknown): Record<string, any> {
  const next = parseSceneMeta(meta)
  delete next.adjustments
  delete next.adjust_filters
  delete next.adjust_preset
  delete next.adjust_applied_at
  return next
}

export function sceneAdjustmentUniformValues(adjustments: SceneAdjustments) {
  return {
    enabled: hasRuntimeSceneAdjustments(adjustments) ? 1 : 0,
    exposure: adjustments.exposure / 50,
    brightness: adjustments.brightness / 100,
    contrast: adjustments.contrast / 100,
    vibrance: adjustments.vibrance / 100,
    saturation: adjustments.saturation / 100,
    warmth: adjustments.warmth / 100,
    sharpness: adjustments.sharpness / 100,
  }
}

function srgbToLinearChannel(value: number): number {
  const clamped = Math.max(0, Math.min(1, value))
  return clamped <= 0.04045 ? clamped / 12.92 : Math.pow((clamped + 0.055) / 1.055, 2.4)
}

function linearToSrgbChannel(value: number): number {
  const clamped = Math.max(0, Math.min(1, value))
  return clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055
}

export function applySceneAdjustmentsToCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  adjustments: SceneAdjustments,
  options: { includeSharpness?: boolean } = {},
): void {
  if (!hasAnySceneAdjustments(adjustments)) return

  const imageData = ctx.getImageData(0, 0, width, height)
  const d = imageData.data
  const exposureFactor = Math.pow(2, adjustments.exposure / 50)
  const brightness = adjustments.brightness / 100
  const contrast = adjustments.contrast / 100
  const contrastFactor = (259 * (contrast * 255 + 255)) / (255 * (259 - contrast * 255))
  const vibrance = adjustments.vibrance / 100
  const saturation = adjustments.saturation / 100
  const warmth = adjustments.warmth / 100

  for (let i = 0; i < d.length; i += 4) {
    let r = d[i] / 255
    let g = d[i + 1] / 255
    let b = d[i + 2] / 255

    if (adjustments.exposure !== 0) {
      r = linearToSrgbChannel(srgbToLinearChannel(r) * exposureFactor)
      g = linearToSrgbChannel(srgbToLinearChannel(g) * exposureFactor)
      b = linearToSrgbChannel(srgbToLinearChannel(b) * exposureFactor)
    }

    r += brightness
    g += brightness
    b += brightness

    r = contrastFactor * (r - 0.5) + 0.5
    g = contrastFactor * (g - 0.5) + 0.5
    b = contrastFactor * (b - 0.5) + 0.5

    let gray = 0.2989 * r + 0.587 * g + 0.114 * b
    const maxChannel = Math.max(r, g, b)
    const minChannel = Math.min(r, g, b)
    const colorSaturation = Math.max(0, maxChannel - minChannel)
    const vibranceScale = vibrance >= 0
      ? 1 + vibrance * (1 - colorSaturation)
      : 1 + vibrance
    r = gray + (r - gray) * vibranceScale
    g = gray + (g - gray) * vibranceScale
    b = gray + (b - gray) * vibranceScale

    gray = 0.2989 * r + 0.587 * g + 0.114 * b
    r = gray + (r - gray) * (1 + saturation)
    g = gray + (g - gray) * (1 + saturation)
    b = gray + (b - gray) * (1 + saturation)

    r += 0.07843137 * warmth
    b -= 0.07843137 * warmth

    d[i] = Math.max(0, Math.min(255, r * 255))
    d[i + 1] = Math.max(0, Math.min(255, g * 255))
    d[i + 2] = Math.max(0, Math.min(255, b * 255))
  }

  ctx.putImageData(imageData, 0, 0)

  if (options.includeSharpness !== false && adjustments.sharpness > 0) {
    applySharpen(ctx, width, height, adjustments.sharpness)
  }
}

export function applySharpen(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  amount: number,
): void {
  const strength = Math.max(0, Math.min(1, amount / 100))
  if (!strength || width < 3 || height < 3) return

  const source = ctx.getImageData(0, 0, width, height)
  const src = source.data
  const output = ctx.createImageData(width, height)
  const dst = output.data
  dst.set(src)

  const rowStride = width * 4
  const center = 1 + 4 * strength
  const side = -strength

  for (let y = 1; y < height - 1; y++) {
    const row = y * rowStride
    for (let x = 1; x < width - 1; x++) {
      const idx = row + x * 4
      for (let channel = 0; channel < 3; channel++) {
        const value =
          center * src[idx + channel] +
          side * (
            src[idx - 4 + channel] +
            src[idx + 4 + channel] +
            src[idx - rowStride + channel] +
            src[idx + rowStride + channel]
          )
        dst[idx + channel] = Math.max(0, Math.min(255, value))
      }
    }
  }

  ctx.putImageData(output, 0, 0)
}

export const SCENE_ADJUSTMENT_SHADER = `
uniform float sceneAdjustmentEnabled;
uniform float sceneAdjustmentExposure;
uniform float sceneAdjustmentBrightness;
uniform float sceneAdjustmentContrast;
uniform float sceneAdjustmentVibrance;
uniform float sceneAdjustmentSaturation;
uniform float sceneAdjustmentWarmth;
uniform float sceneAdjustmentSharpness;
uniform vec2 sceneAdjustmentTexelSize;

vec3 sceneAdjustmentLinearToSrgb(vec3 value) {
  value = clamp(value, 0.0, 1.0);
  return mix(value * 12.92, 1.055 * pow(value, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), value));
}

vec3 sceneAdjustmentSrgbToLinear(vec3 value) {
  value = clamp(value, 0.0, 1.0);
  return mix(value / 12.92, pow((value + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), value));
}

vec3 sceneApplyAdjustments(vec3 linearColor) {
  if (sceneAdjustmentEnabled < 0.5) return linearColor;

  vec3 color = sceneAdjustmentLinearToSrgb(linearColor);

#ifdef USE_MAP
  if (sceneAdjustmentSharpness > 0.0001 && sceneAdjustmentTexelSize.x > 0.0 && sceneAdjustmentTexelSize.y > 0.0) {
    vec2 texel = sceneAdjustmentTexelSize;
    vec3 leftColor = sceneAdjustmentLinearToSrgb(texture2D(map, vMapUv - vec2(texel.x, 0.0)).rgb);
    vec3 rightColor = sceneAdjustmentLinearToSrgb(texture2D(map, vMapUv + vec2(texel.x, 0.0)).rgb);
    vec3 upColor = sceneAdjustmentLinearToSrgb(texture2D(map, vMapUv + vec2(0.0, texel.y)).rgb);
    vec3 downColor = sceneAdjustmentLinearToSrgb(texture2D(map, vMapUv - vec2(0.0, texel.y)).rgb);
    color = color * (1.0 + 4.0 * sceneAdjustmentSharpness) -
      (leftColor + rightColor + upColor + downColor) * sceneAdjustmentSharpness;
  }
#endif

  if (abs(sceneAdjustmentExposure) > 0.0001) {
    vec3 exposedLinear = sceneAdjustmentSrgbToLinear(color) * pow(2.0, sceneAdjustmentExposure);
    color = sceneAdjustmentLinearToSrgb(exposedLinear);
  }

  color += vec3(sceneAdjustmentBrightness);

  float c = sceneAdjustmentContrast;
  float factor = (259.0 * (c * 255.0 + 255.0)) / (255.0 * (259.0 - c * 255.0));
  color = factor * (color - vec3(0.5)) + vec3(0.5);

  float gray = dot(color, vec3(0.2989, 0.587, 0.114));
  float colorMax = max(max(color.r, color.g), color.b);
  float colorMin = min(min(color.r, color.g), color.b);
  float colorSaturation = max(0.0, colorMax - colorMin);
  float vibranceScale = sceneAdjustmentVibrance >= 0.0
    ? 1.0 + sceneAdjustmentVibrance * (1.0 - colorSaturation)
    : 1.0 + sceneAdjustmentVibrance;
  color = vec3(gray) + (color - vec3(gray)) * vibranceScale;

  gray = dot(color, vec3(0.2989, 0.587, 0.114));
  color = vec3(gray) + (color - vec3(gray)) * (1.0 + sceneAdjustmentSaturation);

  color.r += 0.07843137 * sceneAdjustmentWarmth;
  color.b -= 0.07843137 * sceneAdjustmentWarmth;

  return sceneAdjustmentSrgbToLinear(clamp(color, 0.0, 1.0));
}
`

export function patchSceneAdjustmentFragmentShader(fragmentShader: string): string {
  if (fragmentShader.includes('sceneApplyAdjustments')) return fragmentShader

  const withHelpers = fragmentShader.replace('void main() {', `${SCENE_ADJUSTMENT_SHADER}\nvoid main() {`)
  if (withHelpers.includes('#include <color_fragment>')) {
    return withHelpers.replace(
      '#include <color_fragment>',
      '#include <color_fragment>\n\tdiffuseColor.rgb = sceneApplyAdjustments(diffuseColor.rgb);',
    )
  }

  return withHelpers.replace(
    '#include <map_fragment>',
    '#include <map_fragment>\n\tdiffuseColor.rgb = sceneApplyAdjustments(diffuseColor.rgb);',
  )
}
